import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../lib/api/client';
import { JDProcessingInitiated, JDProcessingStatus } from '../lib/types/api';

interface UseJDProcessingOptions {
  pollingInterval?: number; // milliseconds
  autoStart?: boolean;
  onComplete?: (data: JDProcessingStatus) => void;
  onError?: (error: string) => void;
}

interface UseJDProcessingReturn {
  // State
  isProcessing: boolean;
  currentStatus: JDProcessingStatus | null;
  processingId: string | null;
  progress: number;
  error: string | null;
  
  // Actions
  startProcessing: (data: { file?: File; text?: string }) => Promise<void>;
  checkStatus: (id: string) => Promise<void>;
  stopPolling: () => void;
  reset: () => void;
  
  // Status helpers
  isCompleted: boolean;
  isFailed: boolean;
  isUploading: boolean;
  isParsing: boolean;
}

export const useJDProcessing = (options: UseJDProcessingOptions = {}): UseJDProcessingReturn => {
  const {
    pollingInterval = 10000, // 10 seconds default
    autoStart = false,
    onComplete,
    onError
  } = options;

  // State
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<JDProcessingStatus | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Status helpers
  const isCompleted = currentStatus?.status === 'completed';
  const isFailed = currentStatus?.status === 'failed';
  const isUploading = currentStatus?.stage === 'uploading';
  const isParsing = currentStatus?.stage === 'parsing' || currentStatus?.stage === 'extracting';

  // Start processing
  const startProcessing = useCallback(async (data: { file?: File; text?: string }) => {
    try {
      setError(null);
      setIsProcessing(true);
      setProgress(0);
      
      console.log('🚀 Starting JD processing:', data);
      
      // Initiate async processing
      const response: JDProcessingInitiated = await apiClient.parseJobDescriptionAsync(data);
      
      console.log('✅ Processing initiated:', response);
      
      setProcessingId(response.processing_id);
      setProgress(10);
      
      // Start polling if autoStart is enabled
      if (autoStart) {
        startPolling(response.processing_id);
      }
      
    } catch (err) {
      console.error('❌ Failed to start processing:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start processing';
      setError(errorMessage);
      setIsProcessing(false);
      onError?.(errorMessage);
    }
  }, [autoStart, onComplete, onError]);

  // Check status
  const checkStatus = useCallback(async (id: string) => {
    try {
      console.log('🔍 Checking status for:', id);
      
      const status: JDProcessingStatus = await apiClient.getJDProcessingStatus(id);
      
      console.log('📊 Status received:', status);
      console.log('📊 Status details:', {
        status: status.status,
        stage: status.stage,
        progress: status.progress,
        isCompleted: status.status === 'completed',
        isFailed: status.status === 'failed'
      });
      
      setCurrentStatus(status);
      setProgress(status.progress);
      
      // Handle completion - stop polling immediately
      if (status.status === 'completed') {
        console.log('✅ Processing completed, stopping polling immediately');
        console.log('🛑 Clearing polling interval...');
        
        // Stop polling by clearing the interval
        if (pollingRef.current) {
          console.log('🛑 Polling ref exists, clearing interval');
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          console.log('✅ Polling interval cleared');
        } else {
          console.log('⚠️ No polling ref to clear');
        }
        
        setIsProcessing(false);
        setProgress(100);
        onComplete?.(status);
        console.log('✅ Completion callback executed, exiting checkStatus');
        return; // Exit early to prevent further processing
      }
      
      // Handle failure - stop polling immediately
      if (status.status === 'failed') {
        console.log('❌ Processing failed, stopping polling immediately');
        console.log('🛑 Clearing polling interval...');
        
        // Stop polling by clearing the interval
        if (pollingRef.current) {
          console.log('🛑 Polling ref exists, clearing interval');
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          console.log('✅ Polling interval cleared');
        } else {
          console.log('⚠️ No polling ref to clear');
        }
        
        setIsProcessing(false);
        setError(status.error_message || 'Processing failed');
        onError?.(status.error_message || 'Processing failed');
        console.log('❌ Error callback executed, exiting checkStatus');
        return; // Exit early to prevent further processing
      }
      
      console.log('🔄 Processing still in progress, continuing polling...');
      
    } catch (err) {
      console.error('❌ Failed to check status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to check status';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [onComplete, onError]);

  // Start polling
  const startPolling = useCallback((id: string) => {
    // Don't start polling if status is already completed or failed
    if (currentStatus?.status === 'completed' || currentStatus?.status === 'failed') {
      console.log('🛑 Not starting polling - status is already completed/failed:', currentStatus.status);
      return;
    }
    
    // Stop any existing polling first
    if (pollingRef.current) {
      console.log('🔄 Stopping existing polling before starting new one');
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    console.log('🔄 Starting polling for:', id);
    
    pollingRef.current = setInterval(() => {
      // Double-check that we should still be polling
      if (!pollingRef.current) {
        console.log('🔄 Polling was stopped, skipping status check');
        return;
      }
      
      // Additional safety check - don't poll if we're already completed
      if (currentStatus?.status === 'completed' || currentStatus?.status === 'failed') {
        console.log('🔄 Status already completed/failed, stopping polling');
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        return;
      }
      
      console.log('🔄 Executing status check...');
      checkStatus(id);
    }, pollingInterval);
    
  }, [pollingInterval, checkStatus, currentStatus]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      console.log('⏹️ Stopping polling');
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    } else {
      console.log('⏹️ No polling to stop');
    }
  }, []);

  // Reset state
  const reset = useCallback(() => {
    console.log('🔄 Resetting JD processing state');
    stopPolling();
    setIsProcessing(false);
    setCurrentStatus(null);
    setProcessingId(null);
    setProgress(0);
    setError(null);
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Auto-start polling if we have a processing ID and status is not completed/failed
  useEffect(() => {
    if (autoStart && processingId && !pollingRef.current && !isCompleted && !isFailed) {
      console.log('🔄 Auto-starting polling for processing ID:', processingId);
      startPolling(processingId);
    } else if (autoStart && processingId && (isCompleted || isFailed)) {
      console.log('🛑 Not auto-starting polling - status is already completed/failed');
    }
  }, [autoStart, processingId, startPolling, isCompleted, isFailed]);

  return {
    // State
    isProcessing,
    currentStatus,
    processingId,
    progress,
    error,
    
    // Actions
    startProcessing,
    checkStatus,
    stopPolling,
    reset,
    
    // Status helpers
    isCompleted,
    isFailed,
    isUploading,
    isParsing
  };
};
